import { Accordion } from '@zappyrent/hundop';
import { Trans, useTranslation } from 'gatsby-plugin-react-i18next';
import React from 'react';

import {
  StyledFAQSubtitle,
  StyledH3,
} from '../TextPageLandlord/TextPageLandlord.styles';

const FAQProprietari = (): JSX.Element => {
  const { t } = useTranslation(['a11y', 'product']);

  return (
    <>
      <StyledH3 sm="24px" md="24px" lg="24px" tag="h3">
        FAQ
      </StyledH3>
      <StyledFAQSubtitle tag="strong">Contratto</StyledFAQSubtitle>
      <Accordion
        ariaLabel={t('a11y:showHide')}
        autoClose
        content={[
          {
            title: t('product:faqContractQuestion1'),
            body: t('product:faqContractAnswer1'),
          },
          {
            title: t('product:faqContractQuestion2'),
            body: t('product:faqContractAnswer2'),
          },
          {
            title: t('product:faqContractQuestion3'),
            body: (
              <Trans
                components={{
                  a: <a />,
                }}
                i18nKey="faqContractAnswer3"
                t={t}
                values={{ email: 'assistenza@zappyrent.com' }}
              />
            ),
          },
          {
            title: t('product:faqContractQuestion4'),
            body: t('product:faqContractAnswer4'),
          },
          {
            title: `Come posso disdire un contratto di affitto?`,
            body: `La procedura di disdetta della prenotazione o del contratto di affitto segue le disposizioni dettate dalla politica di cancellazione contenuta nel regolamento di Zappyrent. Ti consigliamo di prenderne visione, alla sezione Termini e Condizioni.`,
          },
          {
            title: t('product:faqContractQuestion5'),
            body: (
              <Trans
                components={{
                  a: <a />,
                }}
                i18nKey="faqContractAnswer5"
                t={t}
              />
            ),
          },
        ]}
        id="come-affittare-faq-accordion1"
      />
      <StyledFAQSubtitle tag="strong">Inquilino</StyledFAQSubtitle>
      <Accordion
        ariaLabel={t('a11y:showHide')}
        autoClose
        content={[
          {
            title: `Che tipo di inquilini troverò su Zappyrent?`,
            body: `Gli inquilini che stipulano contratti d’affitto su Zappyrent sono principalmente giovani lavoratori, studenti fuori sede e studenti Erasmus.`,
          },
          {
            title: `Posso conoscere l'inquilino?`,
            body: `Per ciascuna prenotazione riceverai una scheda dettagliata con tutte le informazioni riguardanti la persona che ha richiesto di affittare il tuo immobile.
            Se indicherai nell’annuncio la possibilità di visitare il tuo alloggio e l’inquilino richiederà la visita, l’appuntamento per la visita fisica potrà essere l’occasione di conoscere il potenziale inquilino e facilitare la conclusione dell’accordo da ambo le parti.`,
          },
        ]}
        id="come-affittare-faq-accordion2"
      />
      <StyledFAQSubtitle tag="strong">Annunci</StyledFAQSubtitle>
      <Accordion
        ariaLabel={t('a11y:showHide')}
        autoClose
        content={[
          {
            title: `Pubblicare gli annunci su Zappyrent è gratis?`,
            body: `Sì, gli annunci pubblicati sul nostro portale sono gratuiti.
            Ho inserito un annuncio ma non lo vedo ancora online, come mai?
            Prima della pubblicazione, il tuo annuncio viene valutato dal Team di Zappyrent: se approvato, potrai vederlo online entro 24 ore.`,
          },
          {
            title: `Come posso modificare il mio annuncio?`,
            body: `Puoi farlo accedendo all’area riservata, visualizzando l'annuncio in questione e andando nella sezione 'modifica annuncio subito'.`,
          },
          {
            title: `Come posso eliminare il mio annuncio?`,
            body: `Inviando una mail all’indirizzo assistenza@zappyrent.com, Zappyrent procederà secondo il protocollo 'elimina annuncio subito' ed entro 24 ore il tuo annuncio non sarà più visibile.`,
          },
          {
            title: `Come carico le foto sull'annuncio?`,
            body: `Nella formulazione dell’annuncio, puoi aggiungere tutte le informazioni e i dettagli relativi all’immobile, quindi potrai procedere anche a caricare immagini relative ai locali dell’appartamento, seguendo le istruzioni per la compilazione dell’annuncio.`,
          },
          {
            title: `Perché non vedo la mia risposta sotto la domanda dell'inquilino?`,
            body: `Perché ogni domanda dell’inquilino, quindi anche ogni risposta del proprietario, prima di essere pubblicata viene valutata ed eventualmente approvata dal Team Zappyrent. A questo proposito ricordiamo che non è possibile inserire dati personali nelle risposte.`,
          },
        ]}
        id="come-affittare-faq-accordion3"
      />
      <StyledFAQSubtitle tag="strong">Prenotazioni</StyledFAQSubtitle>
      <Accordion
        ariaLabel={t('a11y:showHide')}
        autoClose
        content={[
          {
            title: `Ho ricevuto un'email con la richiesta della disponibilità dell'appartamento, cosa devo fare?`,
            body: `Ogni volta che un nostro utente si interessa al tuo immobile, riceverai una mail che contiene la richiesta di disponibilità dell’appartamento. Con questo sistema potrai tenere sempre aggiornata la disponibilità del tuo immobile e perfezionare l’accordo di prenotazione nel minor tempo possibile.`,
          },
          {
            title: `Ho ricevuto una richiesta di prenotazione, quanto tempo ho per accettarla o rifiutarla?`,
            body: `Puoi accettare o rifiutare una prenotazione entro 24 ore dal momento in cui ricevi la notifica di una nuova richiesta di disponibilità dell’alloggio.`,
          },
          {
            title: `Ho ricevuto una richiesta di prenotazione con un prezzo più basso o con una data di check-in diversa da quella indicata nell'annuncio. Posso fare una controproposta?`,
            body: `Sì, puoi fare una controproposta all’inquilino: cliccando sul tasto 'Rifiuta', che trovi nei dettagli della richiesta di prenotazione, avrai accesso al box in cui potrai indicare la tua proposta di canone mensile e la data in cui è possibile effettuare il check-in. Se l’inquilino decide di accettare la tua controproposta, la prenotazione verrà automaticamente confermata.`,
          },
          {
            title: `Se accetto una prenotazione riceverò subito il mio affitto?`,
            body: `L’importo relativo alla prima mensilità di affitto ti verrà accreditato 48 ore dopo il check-in dell’inquilino nell’alloggio preso in locazione.`,
          },
          {
            title: `Posso cancellare una prenotazione che ho già accettato?`,
            body: `Sì, puoi cancellare una prenotazione dopo averla accettata, ma in questo caso trovano applicazione le nostre politiche di cancellazione, delle quali ti invitiamo a prendere visione consultando la sezione Termini e Condizioni del sito.`,
          },
        ]}
        id="come-affittare-faq-accordion4"
      />
      <StyledFAQSubtitle tag="strong">Pagamenti</StyledFAQSubtitle>
      <Accordion
        ariaLabel={t('a11y:showHide')}
        autoClose
        content={[
          {
            title: `Perché tutti i pagamenti passano attraverso Zappyrent?`,
            body: `Per semplificare il metodo di pagamento del canone, Zappyrent si incarica di addebitare ogni mensilità sul conto dell’inquilino e di trasferire, automaticamente e senza ritardi, il relativo importo sul conto del proprietario.`,
          },
          {
            title: `Chi custodisce il deposito cauzionale?`,
            body: `Zappyrent si occupa di 'congelare' la cauzione sull’account dell’inquilino e di restituirlo a quest’ultimo al termine del periodo di locazione, fatti salvi gli eventuali danni, verificati e certificati, all’immobile.`,
          },
          {
            title: `Cosa succede se l'inquilino non paga o lascia l'alloggio prima della data di check-out?`,
            body: `Nell’ipotesi in cui l’inquilino non pagasse il canone di affitto oppure lascia l’immobile prima della data stabilita per il check-out senza rispettare le condizioni per la disdetta anticipata, previste nell’annuncio, ti verrà trasferito l’importo del deposito cauzionale nella misura del danno economico subìto.`,
          },
          {
            title: `C'è la possibilità di avere inquilini morosi?`,
            body: `Su Zappyrent ogni inquilino paga con addebito diretto sul conto: in questo modo il locatore riceve i pagamenti sempre puntuali.`,
          },
          {
            title: `Come tutelarsi se l'inquilino non paga l'affitto?`,
            body: `Nell’ipotesi di inquilino moroso, al locatore viene trasferito l’importo del deposito cauzionale nella misura relativa al danno economico subìto.`,
          },
        ]}
        id="come-affittare-faq-accordion5"
      />
      <StyledFAQSubtitle tag="strong">Danni</StyledFAQSubtitle>
      <Accordion
        ariaLabel={t('a11y:showHide')}
        autoClose
        content={[
          {
            title: `In caso di danni chi paga?`,
            body: `Se il proprietario fornisce le prove di un danno all’appartamento concesso in locazione, causato dall’inquilino, può ottenere il risarcimento del danno subìto nei limiti del deposito cauzionale, ove versato. Zappyrent rimane sempre esclusa da qualunque responsabilità per i danni causati dal locatario, che sarà sempre l’unico tenuto a risponderne.`,
          },
          {
            title: `Come ottenere il rimborso in caso di danni nel mio appartamento?`,
            body: `Per ottenere il risarcimento dei danni causati dall’inquilino all’appartamento preso in locazione, il proprietario deve fornire le prove visive del danno e documentare la spesa affrontata per le riparazioni.`,
          },
        ]}
        id="come-affittare-faq-accordion6"
      />
    </>
  );
};

export default FAQProprietari;
